import React, { useState, useEffect, useRef } from 'react';
import { Save, RotateCcw, Eye, EyeOff, FileText } from 'lucide-react';
import { useOrganizador } from '../../contexts/OrganizadorContext';
import { useLabelCurral } from '../../hooks/useLabelCurral';
import catalogoConfigService from '../../services/catalogoConfigService';
import { useToast } from '../../hooks/useToast';
import ToastContainer from '../../components/ui/ToastContainer';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const CatalogoLeilaoConfig = () => {
  const { organizadorAtivo } = useOrganizador();
  const labelCurral = useLabelCurral();
  const isFluxoCavalo = organizadorAtivo?.fluxo_leilao === 'comissao_cavalo';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toasts, success, error, removeToast } = useToast();

  // Colunas disponíveis. "Qtde" fica escondida no fluxo cavalo: o layout
  // aprovado (1 linha = 1 animal) não usa essa coluna, mas ela continua
  // presente por baixo dos panos em colunas_ativas só pra satisfazer a
  // validação (lote+quantidade são obrigatórias pro schema, compartilhado
  // com o gado) — não afeta o PDF, que nem inclui 'quantidade' na ordem de
  // colunas do cavalo (ORDEM_COLUNAS_CAVALO, backend).
  const colunasDisponiveis = [
    { id: 'ordem', label: 'O.D', obrigatoria: false },
    { id: 'lote', label: 'Lote', obrigatoria: true },
    { id: 'vendedor', label: 'Vendedor/Fazenda', obrigatoria: false },
    ...(isFluxoCavalo ? [] : [{ id: 'quantidade', label: 'Qtde', obrigatoria: true }]),
    { id: 'tipo', label: 'Tipo', obrigatoria: false },
    { id: 'raca', label: 'Raça', obrigatoria: false },
    { id: 'sexo', label: 'Sexo', obrigatoria: false },
    { id: 'idade', label: 'Idade', obrigatoria: false },
    { id: 'peso', label: 'Peso(kg)/@', obrigatoria: false },
    { id: 'curral', label: labelCurral, obrigatoria: false },
    { id: 'observacoes', label: 'Observações', obrigatoria: false },
    { id: 'valor_inicial', label: 'Valor Inicial', obrigatoria: false },
    ...(isFluxoCavalo ? [
      { id: 'animal', label: 'Animal (% + nome + registro)', obrigatoria: false },
      { id: 'data_nascimento', label: 'Nasc.', obrigatoria: false },
      { id: 'pai', label: 'Pai', obrigatoria: false },
      { id: 'mae', label: 'Mãe', obrigatoria: false },
      { id: 'registro', label: 'Registro (coluna separada)', obrigatoria: false },
      { id: 'pelagem', label: 'Pelagem', obrigatoria: false },
      { id: 'percentual_venda', label: '% Venda (coluna separada)', obrigatoria: false },
      { id: 'localizacao', label: 'Localização', obrigatoria: false },
    ] : []),
  ];

  // Fluxo cavalo: colunas focadas na identidade do animal (nome+%, registro,
  // raça, nascimento, genealogia) antes do vendedor — layout aprovado com o
  // usuário. Gado mantém o default de sempre.
  const colunasDefaultGado = ['lote', 'vendedor', 'quantidade', 'tipo', 'raca', 'sexo', 'idade', 'peso', 'curral'];
  const colunasDefaultCavalo = ['lote', 'quantidade', 'animal', 'raca', 'data_nascimento', 'pai', 'mae', 'vendedor', 'observacoes'];
  const colunasDefault = isFluxoCavalo ? colunasDefaultCavalo : colunasDefaultGado;

  const [configuracao, setConfiguracao] = useState({
    colunas_ativas: colunasDefault,
    lotes_por_pagina: 32,
    mostrar_totais: true,
    mostrar_rodape: true,
    modo_peso: 'peso_arroba',
    peso_sem_decimais: false,
    ocultar_total_animais: false,
    regulamento_habilitado: false,
    regulamento_texto: '',
    observacao_sempre_abaixo: false,
    obs_ocultar_linha_abaixo: false,
    ajuste_automatico_colunas: false,
    espacamento_linha: 'normal'
  });

  // Configuração do editor Quill
  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['clean']
    ]
  };

  const quillFormats = [
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'align'
  ];

  // Carregar configuração ao montar o componente
  useEffect(() => {
    if (organizadorAtivo?.id) {
      loadConfig();
    }
  }, [organizadorAtivo]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const config = await catalogoConfigService.getConfig(organizadorAtivo.id);
      setConfiguracao(config);
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleColuna = (colunaId) => {
    const coluna = colunasDisponiveis.find(c => c.id === colunaId);
    if (coluna?.obrigatoria) return;

    const novasColunas = configuracao.colunas_ativas.includes(colunaId)
      ? configuracao.colunas_ativas.filter(id => id !== colunaId)
      : [...configuracao.colunas_ativas, colunaId];
    
    // Verificar limites
    const temObs = novasColunas.includes('observacoes');
    const obsSempreAbaixo = temObs && !!configuracao.observacao_sempre_abaixo;
    // Com "observação sempre abaixo" ela não ocupa coluna na tabela, então libera uma coluna a mais
    const maxColunas = (!temObs || obsSempreAbaixo) ? 10 : 9;

    if (novasColunas.length > maxColunas) {
      error(`Máximo de ${maxColunas} colunas permitidas${temObs && !obsSempreAbaixo ? ' (com Observações)' : ''}`);
      return;
    }

    setConfiguracao(prev => ({
      ...prev,
      colunas_ativas: novasColunas
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const configToSave = {
        ...configuracao,
        mostrar_rodape: true // Sempre true
      };
      await catalogoConfigService.updateConfig(organizadorAtivo.id, configToSave);
      success('Configuração salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar:', error);
      error('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = () => {
    setConfiguracao({
      colunas_ativas: colunasDefault,
      lotes_por_pagina: 32,
      mostrar_totais: true,
      mostrar_rodape: true,
      modo_peso: 'peso_arroba',
      peso_sem_decimais: false,
      ocultar_total_animais: false,
      regulamento_habilitado: false,
      regulamento_texto: '',
      observacao_sempre_abaixo: false,
      obs_ocultar_linha_abaixo: false,
      ajuste_automatico_colunas: false,
      espacamento_linha: 'normal'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl px-6 py-6 mb-6">
          <h1 className="text-2xl font-bold text-white">Configuração do Catálogo Leilão</h1>
          <p className="text-blue-100 mt-1">Personalize as colunas e layout do seu catálogo</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Configuração de Colunas */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Colunas do Catálogo</h2>
              <p className="text-sm text-gray-600 mb-6">Selecione quais colunas devem aparecer no seu catálogo</p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {colunasDisponiveis.map((coluna) => {
                  const isActive = configuracao.colunas_ativas.includes(coluna.id);
                  const isObrigatoria = coluna.obrigatoria;
                  
                  return (
                    <div
                      key={coluna.id}
                      className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all cursor-pointer ${
                        isActive 
                          ? 'border-blue-200 bg-blue-50' 
                          : 'border-gray-200 bg-gray-50'
                      } ${isObrigatoria ? 'opacity-75 cursor-not-allowed' : 'hover:border-blue-300'}`}
                      onClick={() => !isObrigatoria && toggleColuna(coluna.id)}
                    >
                      <div className="flex items-center gap-3">
                        {isActive ? (
                          <Eye className="w-5 h-5 text-blue-600" />
                        ) : (
                          <EyeOff className="w-5 h-5 text-gray-400" />
                        )}
                        <span className={`font-medium ${isActive ? 'text-blue-900' : 'text-gray-600'}`}>
                          {coluna.label}
                        </span>
                      </div>
                      {isObrigatoria && (
                        <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                          Obrigatória
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {configuracao.colunas_ativas.includes('observacoes') && (
                <div className="flex items-center justify-between mt-4 p-3 rounded-lg border-2 border-blue-100 bg-blue-50">
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Observação sempre na linha abaixo
                    </label>
                    <p className="text-xs text-gray-500">
                      Remove a coluna Observação da tabela e exibe o texto completo em uma linha abaixo de cada lote, com fonte maior
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={configuracao.observacao_sempre_abaixo || false}
                    onChange={(e) => setConfiguracao(prev => ({
                      ...prev,
                      observacao_sempre_abaixo: e.target.checked
                    }))}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>
              )}

              {configuracao.colunas_ativas.includes('observacoes') && !configuracao.observacao_sempre_abaixo && (
                <div className="flex items-center justify-between mt-4 p-3 rounded-lg border-2 border-blue-100 bg-blue-50">
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Não mostrar a linha abaixo quando a observação não couber
                    </label>
                    <p className="text-xs text-gray-500">
                      A fonte da observação diminui automaticamente para caber na coluna (até 2 linhas), sem criar a linha extra abaixo do lote
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={configuracao.obs_ocultar_linha_abaixo || false}
                    onChange={(e) => setConfiguracao(prev => ({
                      ...prev,
                      obs_ocultar_linha_abaixo: e.target.checked
                    }))}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>
              )}

              <div className="flex items-center justify-between mt-4 p-3 rounded-lg border-2 border-blue-100 bg-blue-50">
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Ajuste automático do tamanho das colunas
                  </label>
                  <p className="text-xs text-gray-500">
                    A largura de cada coluna se adapta ao conteúdo; o espaço que sobra vai para Observações e Vendedor
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={configuracao.ajuste_automatico_colunas || false}
                  onChange={(e) => setConfiguracao(prev => ({
                    ...prev,
                    ajuste_automatico_colunas: e.target.checked
                  }))}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center justify-between mt-4 p-3 rounded-lg border-2 border-blue-100 bg-blue-50">
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Altura das linhas
                  </label>
                  <p className="text-xs text-gray-500">
                    Espaço acima e abaixo do texto de cada linha do catálogo
                  </p>
                </div>
                <div className="flex gap-1">
                  {[['compacta', 'Compacta'], ['normal', 'Normal'], ['folgada', 'Folgada']].map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setConfiguracao(prev => ({ ...prev, espacamento_linha: id }))}
                      className={`px-3 h-8 rounded-md text-sm font-medium transition-colors ${
                        (configuracao.espacamento_linha || 'normal') === id
                          ? 'bg-blue-600 text-white'
                          : 'bg-white border border-gray-300 hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Configurações Gerais */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Configurações Gerais</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lotes por Página
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600">
                    {configuracao.lotes_por_pagina} (fixo)
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Valor fixo otimizado para impressão</p>
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">
                    Mostrar Totais
                  </label>
                  <input
                    type="checkbox"
                    checked={configuracao.mostrar_totais}
                    onChange={(e) => setConfiguracao(prev => ({
                      ...prev,
                      mostrar_totais: e.target.checked
                    }))}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Ocultar Total de Animais
                    </label>
                    <p className="text-xs text-gray-500">Esconde a quantidade total do evento na impressão</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={configuracao.ocultar_total_animais || false}
                    onChange={(e) => setConfiguracao(prev => ({
                      ...prev,
                      ocultar_total_animais: e.target.checked
                    }))}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Exibição do Peso
                  </label>
                  <div className="flex rounded-lg overflow-hidden border border-gray-300">
                    <button
                      type="button"
                      onClick={() => setConfiguracao(prev => ({ ...prev, modo_peso: 'peso' }))}
                      className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${configuracao.modo_peso === 'peso' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                    >
                      Só Peso
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfiguracao(prev => ({ ...prev, modo_peso: 'peso_arroba' }))}
                      className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${configuracao.modo_peso === 'peso_arroba' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                    >
                      Peso + @
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Aplica no catálogo e na impressão</p>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Peso sem Casas Decimais
                    </label>
                    <p className="text-xs text-gray-500">Exibe "329 kg" em vez de "329,00 kg"</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={configuracao.peso_sem_decimais || false}
                    onChange={(e) => setConfiguracao(prev => ({
                      ...prev,
                      peso_sem_decimais: e.target.checked
                    }))}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>

              </div>
            </div>

            {/* Preview */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
              <div className="text-sm text-gray-600 space-y-2">
                {(() => {
                  const temObs = configuracao.colunas_ativas.includes('observacoes');
                  const obsSempreAbaixo = temObs && !!configuracao.observacao_sempre_abaixo;
                  const maxColunas = (!temObs || obsSempreAbaixo) ? 10 : 9;
                  return (
                    <>
                      <p><strong>Colunas ativas:</strong> {configuracao.colunas_ativas.length}/{maxColunas}</p>
                      <p><strong>Lotes por página:</strong> {configuracao.lotes_por_pagina}</p>
                      <p><strong>Totais:</strong> {configuracao.mostrar_totais ? 'Sim' : 'Não'}</p>
                      {temObs && !obsSempreAbaixo && (
                        <p className="text-yellow-600"><strong>⚠️ Limite:</strong> 9 colunas (com OBS)</p>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Ações */}
            <div className="space-y-3">
              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2 font-medium transition-colors"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Salvando...' : 'Salvar Configuração'}
              </button>

              <button
                onClick={resetToDefault}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-3 rounded-lg flex items-center justify-center gap-2 font-medium transition-colors"
              >
                <RotateCcw className="w-5 h-5" />
                Restaurar Padrão
              </button>
            </div>
          </div>
        </div>

        {/* Seção Regulamento Leilão */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Regulamento do Leilão</h2>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={configuracao.regulamento_habilitado || false}
                onChange={(e) => setConfiguracao(prev => ({
                  ...prev,
                  regulamento_habilitado: e.target.checked
                }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              <span className="ml-2 text-sm font-medium text-gray-700">
                {configuracao.regulamento_habilitado ? 'Habilitado' : 'Desabilitado'}
              </span>
            </label>
          </div>
          
          <p className="text-sm text-gray-600 mb-4">
            Quando habilitado, o regulamento será exibido no final do catálogo do leilão (PDF).
          </p>

          {configuracao.regulamento_habilitado && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <ReactQuill
                theme="snow"
                value={configuracao.regulamento_texto || ''}
                onChange={(value) => setConfiguracao(prev => ({
                  ...prev,
                  regulamento_texto: value
                }))}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Digite ou cole o regulamento do leilão aqui..."
                style={{ minHeight: '200px' }}
              />
            </div>
          )}
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default CatalogoLeilaoConfig;